import axios, { AxiosResponse } from 'axios';
import { RSAOAEPEncryption, PublicKeyResponse } from '../../utils/rsaEncryption';
import apiClient, { API_BASE_URL } from '../api/apiClient';
import { mockAdminProfileImageUrl } from '../../utils/postMockData';

// API 응답 타입 정의

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface LoginResponse {
  code: number;
  status: string;
  message: string;
  data: {
    access_token: string;
  };
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  createdAt?: string;
}

// 요청/응답 인터셉터는 공통 클라이언트(apiClient.ts)에서만 설정합니다.

// 전역 RSA-OAEP 인스턴스
const rsaEncryption = new RSAOAEPEncryption();

// 공개키 조회 API
export const getPublicKey = async (): Promise<string> => {
  try {
    /// console.log('공개키 요청:', `${API_BASE_URL}/api/auth/public-key`);
    
    const response: AxiosResponse<PublicKeyResponse> = await apiClient.get('/api/auth/public-key');
    
    // /// console.log('공개키 응답:', {
    //   status: response.status,
    //   code: response.data.code,
    //   message: response.data.message,
    //   data: response.data.data
    // });
    
    let publicKey = '';
    
    // data에서 공개키 확인
    if (response.data.data) {
      publicKey = response.data.data;
    } 
    // data가 null이면 message에서 [] 안의 공개키 추출
    else if (response.data.message) {
      const match = response.data.message.match(/\[(.*?)\]/);
      if (match && match[1]) {
        publicKey = match[1];
        /// console.log('message에서 공개키 추출 성공');
      }
    }
    
    if (!publicKey) {
      throw new Error('공개키를 찾을 수 없습니다.');
    }
    
    // /// console.log({publicKey})
    // /// console.log('공개키 추출 완료:', publicKey.substring(0, 50) + '...');
    return publicKey;
  } catch (error: any) {
    /// console.error('공개키 조회 실패:', error);
    throw new Error('공개키 조회에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
  }
};

// 비밀번호 암호화
export const encryptPassword = async (password: string): Promise<string> => {
  try {
    // 공개키 가져오기
    const publicKeyData = await getPublicKey();
    
    // 공개키 설정 (Web Crypto API용)
    await rsaEncryption.setPublicKey(publicKeyData);
    
    // 비밀번호 RSA-OAEP 암호화
    const encryptedPassword = await rsaEncryption.encryptData(password);
    
    // /// console.log('RSA-OAEP 비밀번호 암호화 성공');
    return encryptedPassword;
  } catch (error: any) {
    /// console.error('비밀번호 암호화 실패:', error);
    throw error;
  }
};
// 응답 인터셉터도 공통 클라이언트(apiClient.ts)에서 처리합니다.

// 관리자 로그인 API
export const adminLoginApi = async (loginData: LoginRequest): Promise<LoginResponse> => {
  try {
    // 비밀번호 암호화
    const encryptedPassword = await encryptPassword(loginData.password);
    
    // /// console.log('관리자 로그인 요청 시작:', { 
    //   url: `${API_BASE_URL}/api/auth/admin/sign-in`,
    //   email: loginData.email,
    //   rememberMe: loginData.rememberMe,
    //   password: encryptedPassword,
    // });

    // 암호화된 비밀번호로 로그인 데이터 구성
    const encryptedLoginData = {
      ...loginData,
      password: encryptedPassword
    };
    
    // console.log('암호화된 관리자 로그인 데이터:', {
    //   email: encryptedLoginData.email,
    //   rememberMe: encryptedLoginData.rememberMe,
    //   passwordLength: encryptedLoginData.password.length,
    //   passwordPreview: encryptedLoginData.password
    // });
    
    // 로컬에서는 프록시 경유로 쿠키를 localhost에 저장, 운영은 원격으로 직접 호출
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    const loginUrl = isLocal ? '/api/auth/admin/sign-in' : `${API_BASE_URL}/api/auth/admin/sign-in`;
    const response: AxiosResponse<LoginResponse> = await axios.post(
      loginUrl,
      encryptedLoginData,
      { withCredentials: true }
    );
    
    // /// console.log('관리자 로그인 응답:', {
    //   response
    // });
    
    // 토큰을 로컬 스토리지에 저장
    if (response.data.data.access_token) {
      localStorage.setItem('access_token', response.data.data.access_token);
    }
    
    return response.data;
  } catch (error: any) {
    // console.error('관리자 로그인 API 에러:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data,
    //   headers: error.response?.headers
    // });
    
    // 에러 메시지 처리
    let errorMessage = '관리자 로그인에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '관리자 ID 또는 비밀번호가 올바르지 않습니다.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 없습니다.';
    } else if (error.response.status === 429) {
      errorMessage = '너무 많은 로그인 시도입니다. 잠시 후 다시 시도해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 로그인 API
export const loginApi = async (loginData: LoginRequest): Promise<LoginResponse> => {
  try {
    // 비밀번호 암호화
    const encryptedPassword = await encryptPassword(loginData.password);
    
    // /// console.log('로그인 요청 시작:', { 
    //   url: `${API_BASE_URL}/api/auth/sign-in`,
    //   email: loginData.email,
    //   rememberMe: loginData.rememberMe,
    //   password: encryptedPassword,
    // });

    // 암호화된 비밀번호로 로그인 데이터 구성
    const encryptedLoginData = {
      ...loginData,
      password: encryptedPassword
    };
    
    // /// console.log('암호화된 로그인 데이터:', {
    //   email: encryptedLoginData.email,
    //   rememberMe: encryptedLoginData.rememberMe,
    //   passwordLength: encryptedLoginData.password.length,
    //   passwordPreview: encryptedLoginData.password
    // });
    
    // /// console.log('요청 헤더 및 설정:', {
    //   baseURL: API_BASE_URL,
    //   headers: apiClient.defaults.headers,
    //   withCredentials: apiClient.defaults.withCredentials
    // });
    
    // 로컬에서는 프록시 경유로 쿠키를 localhost에 저장, 운영은 원격으로 직접 호출
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    const loginUrl = isLocal ? '/api/auth/sign-in' : `${API_BASE_URL}/api/auth/sign-in`;
    const response: AxiosResponse<LoginResponse> = await axios.post(
      loginUrl,
      encryptedLoginData,
      { withCredentials: true }
    );
    
    /// console.log('로그인 응답:', {response });
    
    // 토큰을 로컬 스토리지에 저장
    if (response.data.data.access_token) {
      localStorage.setItem('access_token', response.data.data.access_token);
    }
    
    return response.data;
  } catch (error: any) {
    //  console.error('로그인 API 에러:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data,
    //   headers: error.response?.headers
    // });
    
    // 에러 메시지 처리
    let errorMessage = '로그인에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
    } else if (error.response.status === 429) {
      errorMessage = '너무 많은 로그인 시도입니다. 잠시 후 다시 시도해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 사용자 프로필 조회 API >> 없는데 ?? 
// export const fetchUserProfile = async (): Promise<User> => {
//   try {
//     const response: AxiosResponse<{data: User}> = await apiClient.get('/api/auth/profile');
//     return response.data.data;
//   } catch (error: any) {
//     const errorMessage = error.response?.data?.message || error.message || '프로필 조회에 실패했습니다.';
//     throw new Error(errorMessage);
//   }
// };

// 로그아웃 API
export const logoutApi = async (): Promise<void> => {
  try {
    //  console.log('로그아웃 요청 시작:', {
    //   url: `${API_BASE_URL}/api/auth/sign-out`
    // });
    
    // 로컬에서는 프록시 경유, 운영은 원격으로 직접 호출
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    const logoutUrl = isLocal ? '/api/auth/sign-out' : `${API_BASE_URL}/api/auth/sign-out`;
    const response = await axios.post(logoutUrl, {}, { withCredentials: true });
    
    // /// console.log('로그아웃 응답:', {response
    // });
  } catch (error: any) {
    // console.error('로그아웃 API 호출 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data
    // });
    
    // 로그아웃은 로컬에서 토큰만 제거해도 됨
    /// console.warn('로그아웃 API 호출 실패, 로컬 토큰만 제거합니다.');
  } finally {
    // 로컬 스토리지에서 토큰 제거
    localStorage.removeItem('access_token');
    
    // /// console.log('액세스 토큰이 로컬 스토리지에서 제거되었습니다.');
  }
};

// 토큰 유효성 검사
// 서버에 별도의 /api/auth/validate 엔드포인트는 없습니다.
// 인증이 필요한 실제 요청을 1회 보내 성공하면 세션이 유효한 것으로 간주합니다.
// (access token이 만료되었더라도 refresh 쿠키가 유효하면 JwtAuthFilter가 자동 재발급하여 200으로 응답합니다.)
export const validateToken = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/api/persons/info');
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    return false;
  }
};

// 비밀번호 찾기 관련 타입 정의
export interface SendEmailRequest {
  email: string;
}

export interface SendEmailResponse {
  code: number;
  status: string;
  message: string;
  data: string;
}

export interface VerifyCodeRequest {
  email: string;
  authCode: string;
}

export interface VerifyCodeResponse {
  code: number;
  status: string;
  message: string;
  data: boolean;
}

export interface ResetPasswordRequest {
  email: string;
  new_password: string;
}

export interface ResetPasswordResponse {
  code: number;
  status: string;
  message: string;
  data: {};
}

// 비밀번호 찾기 - 이메일 전송 API
export const sendPasswordResetEmail = async (email: string): Promise<SendEmailResponse> => {
  try {
    // console.log('비밀번호 찾기 이메일 전송 요청:', {
    //   url: `${API_BASE_URL}/api/auth/find-password/send-email`,
    //   email
    // });

    const response: AxiosResponse<SendEmailResponse> = await apiClient.post('/api/auth/find-password/send-email', {
      email
    });

    /// console.log('비밀번호 찾기 이메일 전송 응답:', {response});

    return response.data;
  } catch (error: any) {
    // console.error('비밀번호 찾기 이메일 전송 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data
    // });

    let errorMessage = '이메일 전송에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = error.message || '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '등록되지 않은 이메일입니다.';
    } else if (error.response.status === 404) {
      errorMessage = error.response.data?.message || '등록되지 않은 이메일입니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 비밀번호 찾기 - 인증번호 검증 API
export const verifyResetCode = async (email: string, authCode: string): Promise<VerifyCodeResponse> => {
  try {
    // console.log('비밀번호 찾기 인증번호 검증 요청:', {
    //   url: `${API_BASE_URL}/api/auth/find-password/verify-code`,
    //   email,
    //   authCode
    // });

    const response: AxiosResponse<VerifyCodeResponse> = await apiClient.post('/api/auth/find-password/verify-code', {
      email,
      authCode
    });

    /// console.log('비밀번호 찾기 인증번호 검증 응답:', {response});

    return response.data;
  } catch (error: any) {
    // console.error('비밀번호 찾기 인증번호 검증 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data
    // });

    let errorMessage = '인증번호 검증에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 인증번호입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증번호가 일치하지 않습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 비밀번호 변경 API (로그인한 사용자가 자신의 비밀번호 변경)
export interface ChangePasswordRequest {
  email: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  code: number;
  status: string;
  message: string;
  data?: any;
}

export const changePassword = async (email: string, newPassword: string): Promise<ChangePasswordResponse> => {
  try {
    const encryptedNewPassword = await encryptPassword(newPassword);

    const response: AxiosResponse<ChangePasswordResponse> = await apiClient.patch('/api/auth/change-password', {
      email,
      new_password: encryptedNewPassword,
    });

    return response.data;
  } catch (error: any) {
    let errorMessage = '비밀번호 변경에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = error.response.data?.message || '현재 비밀번호가 올바르지 않습니다.';
    } else if (error.response.status === 403) {
      errorMessage = '비밀번호 변경 권한이 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 비밀번호 재설정 API
export const resetPassword = async (email: string, newPassword: string): Promise<ResetPasswordResponse> => {
  try {
    // 비밀번호 암호화
    const encryptedPassword = await encryptPassword(newPassword);
    
    console.log('비밀번호 재설정 요청:', {
      url: `${API_BASE_URL}/api/auth/reset-password`,
      email,
      passwordLength: encryptedPassword.length
    });

    const response: AxiosResponse<ResetPasswordResponse> = await apiClient.put('/api/auth/reset-password', {
      email,
      new_password: encryptedPassword
    });

    /// console.log('비밀번호 재설정 응답:', {response});

    return response.data;
  } catch (error: any) {
     console.error('비밀번호 재설정 실패:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });

    let errorMessage = '비밀번호 재설정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 만료되었습니다. 다시 시도해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 사용자 정보 관련 타입 정의
export interface UserInfo {
  id: number;
  name: string;
  birth: string;
  phone: string;
  email: string;
  identity: 'STUDENT' | 'DOCTOR' | 'PROFESSOR' | 'OTHER';
  role: string;
  introduction: string;
  image_url: string;
  id_card_file_url: string;
  university: string;
  medicine: string;
  license: string;
  marketing_agreed: boolean;
  approved: string;
}

export interface UserInfoResponse {
  code: number;
  status: string;
  message: string;
  data: UserInfo;
}

// 사용자 정보 조회 API
export const getUserInfo = async (): Promise<UserInfo> => {
  try {
    // /// console.log('사용자 정보 조회 요청:', {
    //   url: `${API_BASE_URL}/api/persons/info`
    // });

    const response: AxiosResponse<UserInfoResponse> = await apiClient.get('/api/persons/info');
    if(response.data.data.role === 'ADMIN') {
      response.data.data.image_url = mockAdminProfileImageUrl;
      response.data.data.name = '투비닥터';
      response.data.data.introduction = '관리자';
      response.data.data.university = '관리자';
    }
    // /// console.log('사용자 정보 조회 응답:', {response
    // });

    return response.data.data;
  } catch (error: any) {
    // console.error('사용자 정보 조회 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data
    // });

    let errorMessage = '사용자 정보 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 404) {
      errorMessage = '사용자 정보를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 프로필 이미지 업로드 API
export const uploadProfileImage = async (imageFile: File): Promise<UserInfo> => {
  try {
    /// console.log('uploadProfileImage 함수 시작');
    
    const formData = new FormData();
    formData.append('image', imageFile);

    // console.log('FormData 생성 완료:', {
    //   url: `${API_BASE_URL}/api/persons/profile-image`,
    //   fileName: imageFile.name,
    //   fileSize: imageFile.size,
    //   fileType: imageFile.type
    // });

    // Authorization 헤더 확인
    const token = localStorage.getItem('access_token');
    /// console.log('토큰 확인:', token ? '토큰 존재' : '토큰 없음');

    const response: AxiosResponse<UserInfoResponse> = await apiClient.patch('/api/persons/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    /// console.log('프로필 이미지 업로드 응답:', {response });

    if (response.data.code !== 200) {
      throw new Error(`서버 에러: ${response.data.message}`);
    }

    return response.data.data;
  } catch (error: any) {
    //  console.error('프로필 이미지 업로드 실패 - 상세 정보:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data,
    //   config: {
    //     url: error.config?.url,
    //     method: error.config?.method,
    //     headers: error.config?.headers
    //   }
    // });

    let errorMessage = '프로필 이미지 업로드에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 이미지 파일입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 405) {
      errorMessage = '서버에서 지원하지 않는 요청 방식입니다.';
    } else if (error.response.status === 413) {
      errorMessage = '이미지 파일이 너무 큽니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 프로필 이미지 삭제 API
export const deleteProfileImage = async (): Promise<void> => {
  try {
  //  console.log('프로필 이미지 삭제 요청:', {
  //     url: `${API_BASE_URL}/api/persons/profile-image`
  //   });

    const response = await apiClient.delete('/api/persons/profile-image');

    /// console.log('프로필 이미지 삭제 응답:', {response});
  } catch (error: any) {
    //  console.error('프로필 이미지 삭제 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data
    // });

    let errorMessage = '프로필 이미지 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 기본정보 수정 API
export const updateBasicInfo = async (data: {
  name: string;
  birth: string;
  phone: string;
}): Promise<UserInfo> => {
  try {
    // console.log('기본정보 수정 요청:', {
    //   url: `${API_BASE_URL}/api/me/private-info`,
    //   data
    // });

    const response: AxiosResponse<UserInfoResponse> = await apiClient.put('/api/me/private-info', data);

    /// console.log('기본정보 수정 응답:', {response});

    if (response.data.code !== 200) {
      throw new Error(`서버 에러: ${response.data.message}`);
    }

    return response.data.data;
  } catch (error: any) {
  //  console.error('기본정보 수정 실패:', {
  //     message: error.message,
  //     status: error.response?.status,
  //     statusText: error.response?.statusText,
  //     data: error.response?.data
  //   });

    let errorMessage = '기본정보 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 입력 정보입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 토큰 관리 유틸리티 함수들

// 액세스 토큰 가져오기
export const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};

// 액세스 토큰 저장
export const setAccessToken = (token: string): void => {
  localStorage.setItem('access_token', token);
};

// 토큰 제거
export const removeTokens = (): void => {
  localStorage.removeItem('access_token');
};

// 토큰이 있는지 확인
export const hasToken = (): boolean => {
  return !!localStorage.getItem('access_token');
};

// 토큰 갱신 (쿠키의 refresh token 사용)
//
// 서버에는 별도의 refresh 엔드포인트가 없습니다. 대신 JwtAuthFilter가 인증이 필요한 모든 요청에서
// access token이 만료/누락된 경우 refresh 쿠키(HttpOnly)로 토큰을 자동 재발급합니다.
// 따라서 인증이 필요한 실제 요청을 1회 보내면 됩니다.
//  - 성공(2xx): 세션 유효. 새 access token은 응답 인터셉터(apiClient.ts)가 'Authorization' 헤더에서 받아 저장합니다.
//  - 실패(401/403): refresh 쿠키도 만료/무효 → 재로그인 필요.
export const refreshToken = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/api/persons/info');
    return response.status >= 200 && response.status < 300;
  } catch (error: any) {
    return false;
  }
};

// 앱 초기화 시 인증 상태 확인 (필요 시 서버가 쿠키로 토큰 자동 갱신)
export const initializeAuth = async (): Promise<boolean> => {
  try {
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

    // 로컬 환경에서는 기존 토큰이 있으면 그대로 사용
    if (isLocal) {
      return hasToken();
    }

    // 운영 환경: 토큰 유무와 관계없이 인증 요청을 1회 보내면
    // 서버(JwtAuthFilter)가 refresh 쿠키로 토큰을 자동 갱신/검증합니다.
    return await refreshToken();
  } catch (error) {
    removeTokens();
    return false;
  }
};

// 회원 자격 인증 API 관련 타입 정의
export interface IdentityVerificationRequest {
  identity: 'STUDENT' | 'DOCTOR';
  department: string;
  idCardFile?: File;
  licenseNumber?: string;
}

export interface IdentityVerificationResponse {
  code: number;
  status: string;
  message: string;
  data: {
    identity: 'STUDENT' | 'DOCTOR';
    department: string;
    id_card_file?: string;
    license_number?: string;
  };
}

// 회원 자격 인증 API
export const verifyIdentity = async (data: IdentityVerificationRequest): Promise<IdentityVerificationResponse> => {
  try {
    console.log('회원 자격 인증 요청:', {
      url: `${API_BASE_URL}/api/persons/identity`,
      identity: data.identity,
      department: data.department,
      hasFile: !!data.idCardFile,
      licenseNumber: data.licenseNumber ? '****' + data.licenseNumber.slice(-4) : undefined
    });

    const formData = new FormData();
    formData.append('identity', data.identity);
    formData.append('department', data.department);
    
    // 의대생인 경우 학생증/재학증명서 파일 필수
    if (data.identity === 'STUDENT' && data.idCardFile) {
      formData.append('idCardFile', data.idCardFile);
    }
    
    // 의사인 경우 면허번호 필수
    if (data.identity === 'DOCTOR' && data.licenseNumber) {
      formData.append('licenseNumber', data.licenseNumber);
    }

    const response: AxiosResponse<IdentityVerificationResponse> = await apiClient.put('/api/persons/identity', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    /// console.log('회원 자격 인증 응답:', {response});

    if (response.data.code !== 200) {
      throw new Error(`서버 에러: ${response.data.message}`);
    }

    return response.data;
  } catch (error: any) {
    // console.error('회원 자격 인증 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   data: error.response?.data,
    //   config: {
    //     url: error.config?.url,
    //     method: error.config?.method,
    //     headers: error.config?.headers
    //   }
    // });

    let errorMessage = '회원 자격 인증에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '입력 정보를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 413) {
      errorMessage = '업로드 파일이 너무 큽니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

export default apiClient;